import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaTimes, FaCode, FaLock } from 'react-icons/fa';
import { useCloseOnEscape } from '../hooks/useCloseOnEscape.ts';
import { API_BASE } from '../utils/apiEndpoints.js';
import { problemPath } from '../utils/problemUrls.ts';
import './css/TheoryPracticeModal.css';

type PracticeProblem = {
  id: number;
  title: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  topic: string;
  slug?: string | null;
  is_premium: boolean;
};

type ModalCopy = {
  title: string;
  subtitle: string;
  close: string;
  loading: string;
  empty: string;
  error: string;
  openTask: string;
  difficulties: Record<PracticeProblem['difficulty'], string>;
};

const COPY: Record<'ru' | 'en', ModalCopy> = {
  ru: {
    title: 'Задачи по теме',
    subtitle: 'Выберите задачу, чтобы перейти к практике',
    close: 'Закрыть',
    loading: 'Загрузка задач…',
    empty: 'Для этой темы пока нет привязанных задач',
    error: 'Не удалось загрузить список задач',
    openTask: 'Открыть задачу',
    difficulties: {
      Easy: 'Лёгкая',
      Medium: 'Средняя',
      Hard: 'Сложная',
    },
  },
  en: {
    title: 'Practice tasks',
    subtitle: 'Choose a task to start practicing',
    close: 'Close',
    loading: 'Loading tasks…',
    empty: 'No tasks are linked to this topic yet',
    error: 'Failed to load tasks',
    openTask: 'Open task',
    difficulties: {
      Easy: 'Easy',
      Medium: 'Medium',
      Hard: 'Hard',
    },
  },
};

type TheoryPracticeModalProps = {
  open: boolean;
  onClose: () => void;
  lessonId: number | null;
  lessonTitle: string;
  lang: string;
};

function difficultyClass(difficulty: PracticeProblem['difficulty']): string {
  switch (difficulty) {
    case 'Easy':
      return 'theory-practice-difficulty--easy';
    case 'Medium':
      return 'theory-practice-difficulty--medium';
    case 'Hard':
      return 'theory-practice-difficulty--hard';
    default:
      return '';
  }
}

export function TheoryPracticeModal({
  open,
  onClose,
  lessonId,
  lessonTitle,
  lang,
}: TheoryPracticeModalProps) {
  const copy = COPY[lang === 'ru' ? 'ru' : 'en'];
  const [problems, setProblems] = useState<PracticeProblem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useCloseOnEscape(open, onClose);

  useEffect(() => {
    if (!open || lessonId == null) return;

    let cancelled = false;

    const loadProblems = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `${API_BASE}/api/theory/lessons/${lessonId}/problems?lang=${encodeURIComponent(lang)}`,
          { credentials: 'include' }
        );

        if (!response.ok) {
          throw new Error('fetch failed');
        }

        const data = (await response.json()) as { problems: PracticeProblem[] };
        if (!cancelled) {
          setProblems(data.problems ?? []);
        }
      } catch {
        if (!cancelled) {
          setProblems([]);
          setError(copy.error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadProblems();

    return () => {
      cancelled = true;
    };
  }, [open, lessonId, lang, copy.error]);

  if (!open) return null;

  return (
    <div
      className="theory-practice-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="theory-practice-title"
    >
      <div className="theory-practice-modal" onClick={(event) => event.stopPropagation()}>
        <div className="theory-practice-head">
          <div className="theory-practice-head-main">
            <span className="theory-practice-icon" aria-hidden>
              <FaCode />
            </span>
            <div>
              <h2 id="theory-practice-title" className="theory-practice-title">
                {copy.title}
              </h2>
              <p className="theory-practice-subtitle">
                {lessonTitle ? `${copy.subtitle}: ${lessonTitle}` : copy.subtitle}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="theory-practice-close"
            onClick={onClose}
            aria-label={copy.close}
          >
            <FaTimes aria-hidden />
          </button>
        </div>

        <div className="theory-practice-body">
          {loading && <p className="theory-practice-state">{copy.loading}</p>}
          {!loading && error && <p className="theory-practice-state theory-practice-state--error">{error}</p>}
          {!loading && !error && problems.length === 0 && (
            <p className="theory-practice-state">{copy.empty}</p>
          )}
          {!loading && !error && problems.length > 0 && (
            <ul className="theory-practice-list">
              {problems.map((problem) => (
                <li key={problem.id}>
                  <Link
                    to={problemPath(problem)}
                    className="theory-practice-link"
                    onClick={onClose}
                  >
                    <span className="theory-practice-link-main">
                      <span className="theory-practice-link-title">{problem.title}</span>
                      <span className="theory-practice-link-meta">
                        <span className="theory-practice-topic">{problem.topic}</span>
                        <span
                          className={`theory-practice-difficulty ${difficultyClass(problem.difficulty)}`}
                        >
                          {copy.difficulties[problem.difficulty] ?? problem.difficulty}
                        </span>
                      </span>
                    </span>
                    {problem.is_premium && (
                      <span className="theory-practice-premium" aria-hidden>
                        <FaLock />
                      </span>
                    )}
                    <span className="theory-practice-arrow" aria-hidden>
                      →
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
